import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

const RadioQuestion = ({ question, value, onChange, error }) => {
  const options = question.options || [];
  const hasOtherOption = question.has_other_option;
  const otherOptionLabel = question.other_option_label || 'Other';
  const otherInputRef = useRef(null);
  
  // Track if user has selected "Other" option specifically
  const [otherSelected, setOtherSelected] = useState(false);
  
  // Check if current value represents an "other" response
  const isOtherValue = hasOtherOption && value && !options.includes(value) && value !== otherOptionLabel;
  
  // The "Other" option should be considered selected if:
  // 1. User explicitly selected it (otherSelected = true), OR
  // 2. Current value is an "other" response (not in predefined options)
  const isOtherSelected = otherSelected || isOtherValue;
  
  // Initialize otherText based on current value
  const [otherText, setOtherText] = useState(isOtherValue ? value : '');
  
  // Update otherText when value changes from outside
  useEffect(() => {
    if (isOtherValue && value !== otherText) {
      setOtherText(value);
      setOtherSelected(true);
    } else if (!isOtherSelected && value && options.includes(value)) {
      setOtherText('');
      setOtherSelected(false);
    }
  }, [value, isOtherValue, isOtherSelected, otherText, options]);
  
  // Auto-focus the other input when it appears
  useEffect(() => {
    if (isOtherSelected && otherInputRef.current) {
      otherInputRef.current.focus();
    }
  }, [isOtherSelected]);
  
  const handleRadioChange = (selectedValue) => {
    if (selectedValue === otherOptionLabel && hasOtherOption) {
      // If "Other" is selected, mark it as selected and start with empty text
      setOtherSelected(true);
      const initialText = '';
      setOtherText(initialText);
      onChange(initialText);
    } else {
      // For regular options, clear other state and use the selected value
      setOtherSelected(false);
      setOtherText('');
      onChange(selectedValue);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    // Update the form value with the other text
    onChange(text);
  };

  // Determine which radio button should be checked
  const getCheckedValue = () => {
    if (isOtherSelected) {
      return otherOptionLabel;
    }
    return value;
  };

  return (
    <div className="radio-group">
      {options.map((option, index) => {
        const optionId = `${question.id}_${index}`;
        return (
          <div key={index} className="radio-option">
            <input
              type="radio"
              id={optionId}
              name={`question_${question.id}`}
              value={option}
              checked={getCheckedValue() === option}
              onChange={() => handleRadioChange(option)}
            />
            <label htmlFor={optionId}>
              {option}
            </label>
          </div>
        );
      })}
      
      {hasOtherOption && (
        <div className="radio-option">
          <input
            type="radio"
            id={`${question.id}_other`}
            name={`question_${question.id}`}
            value={otherOptionLabel}
            checked={isOtherSelected}
            onChange={() => handleRadioChange(otherOptionLabel)}
          />
          <label htmlFor={`${question.id}_other`}>
            {otherOptionLabel}
          </label>
        </div>
      )}
      
      {hasOtherOption && isOtherSelected && (
        <div style={{ marginLeft: '24px', marginTop: '8px', marginBottom: '16px' }}>
          <input
            ref={otherInputRef}
            type="text"
            value={otherText}
            onChange={(e) => handleOtherTextChange(e.target.value)}
            placeholder="Please specify..."
            className="form-control form-control-sm"
            style={{ maxWidth: '300px' }}
          />
        </div>
      )}
    </div>
  );
};

RadioQuestion.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.number.isRequired,
    options: PropTypes.array,
    has_other_option: PropTypes.bool,
    other_option_label: PropTypes.string
  }).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  error: PropTypes.string
};

export default RadioQuestion;